import { useMutation, useQueryClient } from "@tanstack/react-query";
import invariant from "tiny-invariant";
import { erc20Abi, getContract, parseUnits, type Address } from "viem";
import { waitForTransactionReceipt } from "viem/actions";
import { useAccount, useChainId, useWalletClient } from "wagmi";

import type { Currency } from "#entities/index.ts";
import { usdtABI } from "#abi/index.ts";
import { notifyPromise } from "#ui/toast/notify.tsx";
import { catchError } from "#ui/toast/catch-error.ts";

import { getAllowanceQueryKey } from "./use-allowance-query";

export type UseERC20ApproveMutationParams = {
  currency: Currency;
  contractAddress: Address;
};

export const useERC20ApproveMutation = () => {
  const chainId = useChainId();
  const { data: wc } = useWalletClient({ chainId });
  const { address: accountAddress } = useAccount();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["erc20-approve"] as const,
    mutationFn: async (params: UseERC20ApproveMutationParams) => {
      invariant(wc, "useERC20ApproveMutation. wc is undefined");

      const { contractAddress, currency } = params;
      const contract = getContract({
        abi: currency.symbol === "USDT" ? usdtABI : erc20Abi,
        address: currency.address,
        client: wc,
      });

      const totalSupply = await contract.read.totalSupply();

      const defaultAllowance = parseUnits("100000", 18);

      const {
        request: { args, ...options },
      } = await contract.simulate.approve([
        contractAddress,
        defaultAllowance > totalSupply ? defaultAllowance : totalSupply,
      ]);

      const hash = await contract.write.approve(args, options);

      const receiptPromise = waitForTransactionReceipt(wc, { hash });

      notifyPromise(receiptPromise, {
        pending: "Pending...",
        success: `Spending ${currency.symbol} has been approved`,
      });

      return await receiptPromise;
    },
    onError: catchError,
    onSuccess: async (_, variables) => {
      const { contractAddress, currency } = variables;
      const key = getAllowanceQueryKey({
        accountAddress,
        contractAddress,
        currencyAddress: currency.address,
      });

      const result = await queryClient.invalidateQueries({ queryKey: key });
      return result;
    },
  });
};
